import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MdVerified } from "react-icons/md";
import { FaShareFromSquare } from "react-icons/fa6";

interface Campaign {
  id: number;
  title: string;
  slug: string;
  thumbnail: string;
  target: number;
  collected: number;
  donation_category?: {
    name: string;
  };
  created_by?: {
    name: string;
  };
}

const truncateText = (text: string, max: number) => {
  if (text.length <= max) return text;
  return text.slice(0, max) + "...";
};

const CampaignSection: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTitle, setExpandedTitle] = useState<number | null>(null);
  const IconVerif = MdVerified as React.FC<React.SVGProps<SVGSVGElement>>;
  const IconShare = FaShareFromSquare as React.FC<React.SVGProps<SVGSVGElement>>;

  const handleShare = (item: Campaign) => {
    if (navigator.share) {
      navigator.share({
        title: item.title,
        text: `Ayo bantu campaign ini: ${item.title}`,
        url: `https://belaskasih.com/campaigns/${item.slug}`,
      });
    } else {
      alert("Fitur share tidak didukung di browser ini");
    }
  };

  // useEffect(() => {
  //   window.scrollTo({ top: 0, behavior: "auto" });
  // }, []);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const res = await fetch("https://api.belaskasih.com/api/v1/campaign?status=approved&limit=6");
        const json = await res.json();

        if (json?.data?.data) {
          const filtered = json.data.data.filter((item: Campaign) => /||/i.test(item.title));
          setCampaigns(filtered);
        }
      } catch (error) {
        console.error("Gagal mengambil campaign:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  return (
    <section className="bg-white py-16">
      <div className="max-w-[1440px] mx-auto px-4 md:px-8">
        {/* HEADER */}
        <div className="text-center mb-12 flex flex-col justify-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#131010]">
            Donasi <span className="text-red-500 font-bilbo">Kita</span>
          </h2>
          <p className="mt-2 text-xs md:text-sm max-w-xl mx-auto text-center text-gray-600">Bantu Masyarakat terdampak sosial sistematik diseluruh wilayah Indonesia.</p>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-20 gap-2">
            <span className="w-3 h-3 bg-red-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
            <span className="w-3 h-3 bg-red-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
            <span className="w-3 h-3 bg-red-500 rounded-full animate-bounce" />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {campaigns.map((item) => {
            const percentage = Math.min(Math.round((item.collected / item.target) * 100), 100);

            const isExpanded = expandedTitle === item.id;

            return (
              <div key={item.id} className="relative max-sm:bg-[#faf7f2] bg-white shadow-xl md:shadow-xl rounded-2xl overflow-hidden border">
                {/* GRADIENT */}
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-400 to-white" />

                {/* IMAGE */}
                <img src={item.thumbnail} alt={item.title} className="w-full h-[190px] object-cover" />

                {/* CONTENT */}
                <div className="p-5 text-gray-800 flex flex-col h-full">
                  {/* TITLE (TOGGLE) */}
                  <h3 onClick={() => setExpandedTitle(isExpanded ? null : item.id)} className="text-lg font-bold mb-2 cursor-pointer hover:text-red-400 transition" title={item.title}>
                    {isExpanded ? item.title : truncateText(item.title, 30)}
                  </h3>

                  {/* created by */}
                  <div className="  text-gray-500 text-sm italic  mb-3 flex justify-start items-center gap-2">
                    {item.created_by?.name ?? "-"}
                    <span className="text-red-500">
                      <IconVerif />
                    </span>
                  </div>

                  {/* PROGRESS TEXT */}
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-red-500 font-semibold">Rp {item.collected.toLocaleString("id-ID")}</span>
                    <span className="text-gray-800">{percentage}%</span>
                  </div>

                  {/* PROGRESS BAR */}
                  <div className="w-full h-2 bg-gray-300 rounded-full mb-3">
                    <div className="h-full bg-red-500 rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
                  </div>

                  {/* TARGET */}
                  <div className="flex justify-between items-center mb-6">
                    <p className="text-sm text-gray-500 font-sans italic">
                      Target Pengumpulan: <span className="text-gray-600 font-semibold">Rp {item.target.toLocaleString("id-ID")}</span>
                    </p>

                    <button onClick={() => handleShare(item)} className="hover:text-red-400 text-red-500 transition" title="Bagikan Campaign">
                      <span className="text-[18px]">
                        <IconShare />
                      </span>
                    </button>
                  </div>

                  <div className="flex justify-center">
                    <Link to={`https://belaskasih.com/campaigns/${item.slug}`} target="_blank" rel="noopener noreferrer" className="mt-auto">
                      <div
                        className="px-11 py-2 text-sm font-semibold text-white rounded-lg bg-gradient-to-r
                        from-red-600 via-red-400 to-red-600
                        hover:from-red-700 hover:via-red-600 hover:to-red-700
                        transition-all duration-30">
                        Donasi Sekarang
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* BUTTON */}
        <div className="flex justify-center mt-12">
          <Link
            to="/donasiumum"
            className="
              flex items-center gap-2
              px-6 py-2.5
              border border-red-500
              text-red-500
              rounded-full
              text-sm font-semibold
              hover:bg-red-500 hover:text-white
              transition
            ">
            Lihat Donasi Umum Lainnya →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CampaignSection;
